import { normalizeHTML } from '@/utils/normalizeHTML';
import { initiateSequence } from '@/services/sequencesService';
import { sendAnalytics } from '@/services/analyticsService';
import toast from 'react-hot-toast';
import { useUpdateOnboardingStatus } from '@/modules/onboarding/hooks/useUpdateOnboardingStatus';
import { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useSequencesContext } from '../SequencesContext';
import { useSequences } from './useSequences';

export function useSendAllSequence() {
	const { fetchSequences, resetSequenceState } = useSequences();
	const {
		selectedSequence,
		selectedLists,
		businessesToRemove,
		businessesToInclude,
		modifiedEmails,
		unapprovedBusinesses,
		allBusinessesSelect,
		setSequencePage,
		setActiveTab,
		setShowSendAll,
		setModifiedEmails
	} = useSequencesContext();
	const [isLoading, setIsLoading] = useState(false);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	function closeModal() {
		setShowSendAll(false);
		setModifiedEmails([]);
		resetSequenceState();
	}

	const isUserOnboarding = user?.onboarding?.status === 'pending';
	const { mutateAsync: updateOnboardingStatus } = useUpdateOnboardingStatus();

	const triggerSendAll = async () => {
		if (!selectedSequence?.id) return;

		setIsLoading(true);

		const listIds = selectedLists.map(l => l.id);

		const excludedBusinessIds =
			allBusinessesSelect === 'SELECTED_ALL'
				? [...businessesToRemove, ...unapprovedBusinesses]
				: undefined;

		const includedBusinesses =
			allBusinessesSelect === 'UNSELECTED_ALL'
				? businessesToInclude.filter(
						b => !unapprovedBusinesses.includes(b.businessId)
					)
				: undefined;

		initiateSequence({
			campaignId: selectedSequence.id,
			listIds,
			excludedBusinessIds,
			includedBusinesses,
			modifiedEmails: modifiedEmails.map(m => ({
				...m,
				body: normalizeHTML(m.body)
			}))
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error initiating sequence. Please try again later.'
					);
				}
				const completeOnboarding = async () => {
					if (isUserOnboarding) {
						await updateOnboardingStatus({ status: 'completed' });
						sessionStorage.removeItem('suppressOnboardingRedirect');
					}
				};

				completeOnboarding();
				closeModal();
				setSequencePage('MY-SEQUENCES');
				setActiveTab('SEQUENCE');
				fetchSequences();

				toast.success('Sequence initiated successfully!');
			})
			.catch(() => {
				toast.error('Unexpected error. Please try again later.');
			})
			.finally(() => {
				setIsLoading(false);
			});

		const sharedPayload = {
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id
		};

		const eventNames: string[] = [];

		if (isUserOnboarding) {
			eventNames.push('Onboarding Flow - Clicked Set Sequence Live');
		} else if (selectedSequence.id) {
			eventNames.push('user enrolled businesses sequences');
		} else {
			eventNames.push('user_set_sequence_live');
		}

		eventNames.forEach(eventName => {
			sendAnalytics({
				...sharedPayload,
				eventName
			});
		});
	};

	return { triggerSendAll, isLoading };
}
